import { motion } from "framer-motion";
import { Users, Briefcase, Palette, ArrowRight, Loader2, Info } from "lucide-react";
import { SectionHead } from "./SectionHead";
import { useBooking } from "@/context/BookingContext";
import { useVehicles, vehicleName } from "@/lib/useVehicles";
import { usePublicPricing } from "@/lib/usePublicPricing";

const STATUS = {
  available: { label: "Available", cls: "bg-green-500/15 text-green-400 border border-green-500/30" },
  rented: { label: "Rented", cls: "bg-red-500/15 text-red-400 border border-red-500/30" },
  maintenance: { label: "Maintenance", cls: "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30" },
};

export const Fleet = () => {
  const { openBooking } = useBooking();
  const { vehicles, loading } = useVehicles();
  const pricing = usePublicPricing();
  const availableCount = vehicles.filter((v) => (v.status || "available") === "available").length;

  return (
    <section id="fleet" data-testid="fleet-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <SectionHead overline="The Fleet" title="Pick Your" accent="Ride." desc="Affordable, reliable vehicles ready to roll. Choose Bring-Your-Own-Insurance for the lowest rates or All-In Coverage." />

        {!loading && availableCount === 1 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            data-testid="last-vehicle-banner"
            className="mt-8 flex items-center justify-center gap-2 rounded-xl border border-[#F2B705]/50 bg-[#F2B705]/[0.08] px-5 py-3.5 text-center">
            <span className="text-lg" aria-hidden>🔥</span>
            <span className="font-display font-bold text-[#F2B705] text-sm sm:text-base tracking-wide">
              Only 1 Vehicle Available — Reserve Before It's Gone!
            </span>
          </motion.div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#F2B705]" /></div>
        ) : vehicles.length === 0 ? (
          <p className="text-white/40 mt-10">No vehicles available right now. Please check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {vehicles.map((car, i) => {
              const st = STATUS[car.status] || STATUS.available;
              const isAvailable = car.status === "available";
              return (
                <motion.div
                  key={car.id}
                  data-testid={`fleet-card-${car.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  className="group bg-[#0C0C0C] border border-white/10 rounded-xl overflow-hidden hover:border-[#F2B705]/40 transition-colors duration-300 hover:-translate-y-1 will-change-transform"
                >
                  <div className="relative h-52 overflow-hidden bg-black">
                    {car.images?.[0] ? (
                      <img src={car.images[0]} alt={vehicleName(car)} className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${isAvailable ? "" : "grayscale opacity-50"}`} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20">No photo</div>
                    )}
                    <span className="absolute top-3 left-3 bg-[#F2B705] text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">{car.category}</span>
                    <span className={`absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display font-bold text-xl">{car.year} {vehicleName(car)}</h3>
                        <span className="text-xs uppercase tracking-widest text-white/40">{car.color}</span>
                      </div>
                      <div className="flex items-center gap-3 text-white/55 text-sm pt-1">
                        <span className="flex items-center gap-1.5"><Users size={15} className="text-[#F2B705]" /> {car.seats}</span>
                        <span className="flex items-center gap-1.5"><Briefcase size={15} className="text-[#F2B705]" /> {car.bags}</span>
                      </div>
                    </div>
                    {car.blurb && <p className="text-white/50 text-sm mt-2 font-light">{car.blurb}</p>}

                    {car.premium?.enabled ? (
                      <>
                        <div className="grid grid-cols-2 gap-3 mt-5" data-testid={`fleet-premium-${car.id}`}>
                          <div className="rounded-lg border border-[#F2B705]/40 bg-[#0A0A0A] p-3">
                            <div className="text-[10px] uppercase tracking-widest text-[#F2B705]/80">{parseInt(car.premium.exact_days) || 3}-Day Package</div>
                            <div className="text-gold-gradient font-display font-black text-2xl leading-none mt-1">${car.premium.package_price}</div>
                            <div className="text-xs text-white/45 mt-1">{Number(car.premium.extra_day_rate) > 0 ? `+$${car.premium.extra_day_rate}/extra day` : `exactly ${parseInt(car.premium.exact_days) || 3} days`}</div>
                          </div>
                          {car.premium.packages?.[0]?.price ? (
                            <div className="relative rounded-lg border border-[#F2B705]/40 bg-[#F2B705]/[0.06] p-3">
                              <span className="absolute -top-2 right-2 bg-[#F2B705] text-black text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">Premium</span>
                              <div className="text-[10px] uppercase tracking-widest text-[#F2B705]/80">{car.premium.packages[0].label || `${car.premium.packages[0].days}-Day Package`}</div>
                              <div className="text-white font-display font-black text-2xl leading-none mt-1">${car.premium.packages[0].price}</div>
                              <div className="text-xs text-white/45 mt-1">{Number(car.premium.extra_day_rate) > 0 ? `7 days · +$${car.premium.extra_day_rate}/day after` : `exactly ${car.premium.packages[0].days} days`}</div>
                            </div>
                          ) : (
                            <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 flex items-center">
                              <p className="text-white/45 text-xs leading-relaxed">Fixed-length premium rental — returned on the scheduled date.</p>
                            </div>
                          )}
                        </div>
                        <p className="text-[#F2B705]/80 text-[11px] mt-2">⭐ Premium vehicle — {Number(car.premium.extra_day_rate) > 0 ? "premium rates apply; regular specials & promos don't" : "fixed packages only. Regular specials & promos don't"} apply.</p>
                      </>
                    ) : (
                    <div className="grid grid-cols-2 gap-3 mt-5">
                      <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3">
                        <div className="text-[10px] uppercase tracking-widest text-white/40">3-Day Special</div>
                        <div className="text-gold-gradient font-display font-black text-2xl leading-none mt-1">${pricing.three_day_package}</div>
                        <div className="text-xs text-white/45 mt-1">+${pricing.additional_day_rate}/extra day</div>
                      </div>
                      <div className="relative rounded-lg border border-[#F2B705]/40 bg-[#F2B705]/[0.06] p-3">
                        <span className="absolute -top-2 right-2 bg-[#F2B705] text-black text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">Best Value</span>
                        <div className="text-[10px] uppercase tracking-widest text-[#F2B705]/80">Weekly Special</div>
                        <div className="text-white font-display font-black text-2xl leading-none mt-1">${pricing.weekly_special}</div>
                        <div className="text-xs text-white/45 mt-1">7 days · save ${pricing.weekly_savings}</div>
                      </div>
                    </div>
                    )}

                    <div className="mt-4 rounded-lg border border-white/10 bg-[#0A0A0A] p-3" data-testid={`fleet-coverage-${car.id}`}>
                      <div className="text-[10px] uppercase tracking-widest text-[#F2B705]/80 mb-2">Coverage Options Available</div>
                      <ul className="space-y-1.5 text-xs">
                        <li className="flex items-center justify-between gap-2">
                          <span className="text-white/70">Use My Own Insurance</span>
                          <span className="text-white/50 font-semibold">$0/day</span>
                        </li>
                        <li className="flex items-center justify-between gap-2">
                          <span className="text-white/70">Add All-In Coverage</span>
                          <span className="text-white/50 font-semibold">+${pricing.allin_coverage_day}/day</span>
                        </li>
                        <li className="flex items-center justify-between gap-2">
                          <span className="text-white/70">Decline Optional Insurance</span>
                          <span className="text-white/50 font-semibold">$0/day</span>
                        </li>
                        <li className="flex items-center justify-between gap-2">
                          <span className="text-white/70">Optional Damage Waiver <span className="text-white/40">(not insurance)</span></span>
                          <span className="text-white/50 font-semibold">+${pricing.damage_waiver_day}/day</span>
                        </li>
                      </ul>
                      <p className="text-white/35 text-[11px] mt-2 flex items-center gap-1"><Info size={11} /> Coverage &amp; add-ons are itemized separately at checkout.</p>
                    </div>

                    <button
                      data-testid={`fleet-book-${car.id}`}
                      disabled={!isAvailable}
                      onClick={() => openBooking(vehicleName(car))}
                      className="mt-6 w-full flex items-center justify-center gap-2 border border-white/15 py-3 rounded-md font-semibold group-hover:bg-[#F2B705] group-hover:text-black group-hover:border-[#F2B705] transition-colors duration-300 disabled:opacity-40 disabled:pointer-events-none"
                    >
                      {isAvailable ? <>Request This Car <ArrowRight size={16} /></> : "Currently Unavailable"}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
